import { CalendarClock, Clock, Mic, Radio } from "lucide-react";
import { CardShell, ScheduledBadge, parsePaths, type MetaFact } from "./CardShell";
import { isScheduled, scheduledDateLabel } from "../scheduled";
import { type Dict, type Lang } from "@/lib/i18n";
import { modulePoster } from "@/lib/lecturehub/poster";
import { moduleHref } from "../moduleHref";
import { formatDuration, formatEpisodes } from "@/lib/format";
import type { Module } from "@/lib/lecturehub/types";
import { isFreeToUnlock } from "../paywall";

export function PodcastCard({
  lang,
  t,
  module: m,
  enrolled = false,
  cfHash = null,
  library = false,
}: {
  lang: Lang;
  t: Dict;
  module: Module;
  enrolled?: boolean;
  cfHash?: string | null;
  library?: boolean;
}) {
  const pod = m.podcast;
  const isSeries = pod?.audioType === "simple_series" || pod?.audioType === "section_series";
  const episodeCount = isSeries ? (pod?.audios?.length ?? 0) : 1;
  const totalDuration = isSeries
    ? (pod?.audios ?? []).reduce((sum, a) => sum + (a.audio?.duration ?? 0), 0)
    : (pod?.audio?.duration ?? null);
  const paths = parsePaths(m.keywords, lang);
  const href = moduleHref(lang, m, library);

  const scheduled = isScheduled(m);
  const scheduledLabel = scheduledDateLabel(t.detail, m.publishAt, lang);

  const facts: MetaFact[] = [];
  if (scheduledLabel) {
    facts.push({ Icon: CalendarClock, label: scheduledLabel });
  }
  if (isSeries && episodeCount > 0) {
    facts.push({ Icon: Radio, label: formatEpisodes(episodeCount, lang, t.units) });
  }
  if (totalDuration && totalDuration > 0) {
    facts.push({ Icon: Clock, label: formatDuration(totalDuration) });
  }

  return (
    <CardShell
      t={t}
      href={href}
      openHref={href}
      poster={modulePoster(m, cfHash)}
      posterFallback={t.detail.typePodcast}
      typeLabel={t.detail.typePodcast}
      TypeIcon={Mic}
      statusLabel={scheduled ? undefined : isFreeToUnlock(m.price) ? t.detail.free : undefined}
      posterBadge={scheduled ? <ScheduledBadge t={t} /> : undefined}
      title={m.name ?? "—"}
      abstract={m.abstract}
      enrolled={enrolled}
      paths={paths}
      facts={facts}
    />
  );
}
